import * as Select from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";

const BRAND = "#6C36E7";

export function ActivityOption({ code, label, checked, onToggle }: { code: string; label: string; checked: boolean; onToggle: () => void }) {
    return (
        <label data-testid={`signup-act-card-${code}`}
            className="group relative flex flex-1 min-w-[140px] cursor-pointer items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm transition-[background-color,border-color,box-shadow] duration-200 hover:border-[#6C36E7]/60 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#6C36E7]/50"
            style={{
                borderColor: checked ? BRAND : "var(--border)",
                background: checked ? "rgba(108,54,231,0.12)" : "var(--input)",
                boxShadow: checked ? "0 6px 18px -10px rgba(108,54,231,0.7)" : "none",
                color: "var(--text)", minHeight: 44,
            }}>
            <input type="checkbox" data-testid={`signup-act-${code}`} checked={checked} onChange={onToggle} className="peer sr-only" />
            <span aria-hidden className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-[background-color,border-color,transform] duration-200"
                style={{ borderColor: checked ? BRAND : "var(--text-muted)", background: checked ? BRAND : "transparent", transform: checked ? "scale(1.05)" : "scale(1)" }}>
                <Check size={12} strokeWidth={3} color="#fff" className="transition-opacity duration-200" style={{ opacity: checked ? 1 : 0 }} />
            </span>
            <span className="font-medium">{label}</span>
        </label>
    );
}

const NONE = "__none__";

export function PlanSelect({ value, onChange, options, placeholder }: {
    value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; placeholder: string;
}) {
    const items = [{ value: NONE, label: placeholder }, ...options];
    return (
        <Select.Root value={value || NONE} onValueChange={(v) => onChange(v === NONE ? "" : v)}>
            <Select.Trigger data-testid="signup-plan" aria-label={placeholder}
                className="surface-input group inline-flex w-full items-center justify-between gap-2 px-3 text-sm outline-none transition-[border-color,box-shadow] duration-200 hover:border-[#6C36E7]/60 focus-visible:border-[#6C36E7] focus-visible:ring-2 focus-visible:ring-[#6C36E7]/40 data-[state=open]:border-[#6C36E7] data-[state=open]:ring-2 data-[state=open]:ring-[#6C36E7]/40"
                style={{ minHeight: 44, borderRadius: 12 }}>
                <Select.Value />
                <Select.Icon><ChevronDown size={16} className="transition-transform duration-200 group-data-[state=open]:rotate-180" style={{ color: "var(--text-muted)" }} /></Select.Icon>
            </Select.Trigger>
            <Select.Portal>
                <Select.Content position="popper" sideOffset={6} data-testid="signup-plan-menu"
                    className="auth-select-menu z-[60] w-[var(--radix-select-trigger-width)] overflow-hidden rounded-xl p-1.5 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95">
                    <Select.Viewport>
                        {items.map((o) => (
                            <Select.Item key={o.value} value={o.value} data-testid={`signup-plan-option-${o.value === NONE ? "none" : o.value}`}
                                className="auth-select-item relative flex cursor-pointer select-none items-center justify-between rounded-lg px-3 py-2.5 text-sm outline-none transition-colors">
                                <Select.ItemText>{o.label}</Select.ItemText>
                                <Select.ItemIndicator><Check size={15} color={BRAND} strokeWidth={2.5} /></Select.ItemIndicator>
                            </Select.Item>
                        ))}
                    </Select.Viewport>
                </Select.Content>
            </Select.Portal>
        </Select.Root>
    );
}
